/* eslint-disable no-unused-vars */
import { Button, Group, Image, Stack, Text } from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import React, { useState } from 'react';
import { IconUpload, IconX, IconPhoto } from '@tabler/icons-react';
import { useQuery, useMutation } from '@tanstack/react-query';
import fileToBase64 from 'utils/fileToBase64';
import { sharepointService, UploadSharepointForm } from 'services/sharepoint/sharepoint.service';
import { showErrorNotification, showSuccessNotification } from 'utils/notification';

interface InputMaterialProps {
  onSuccess?: (_data: { title: string; url: string; type: string; description?: string }) => void;
  onCancel?: () => void;
}

const InputMaterial: React.FC<InputMaterialProps> = ({ onSuccess, onCancel }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [hasFileToUpload, setHasFileToUpload] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const { data: sharepointToken } = useQuery({
    queryKey: ['getTokenSharepoint'],
    queryFn: () => sharepointService.getTokenSharepoint(),
    enabled: hasFileToUpload
  });

  const uploadFile = useMutation({
    mutationFn: (data: UploadSharepointForm) => sharepointService.uploadFile(data),
    onSuccess: () => {
      showSuccessNotification({
        title: 'Success upload!',
        message: 'Your file has been uploaded successfully'
      });
    },
    onError: () => {
      showErrorNotification({
        title: 'Failed when upload file',
        message: 'Failed when upload your file, please try again later.'
      });
    }
  });

  const acceptedTypes = {
    'image/png': ['.png'],
    'image/jpeg': ['.jpg', '.jpeg'],
    'video/mp4': ['.mp4'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    'application/pdf': ['.pdf']
  };

  const getFileType = (file: File): string => {
    const mimeType = file.type;
    if (mimeType.startsWith('image/')) return 'Image';
    if (mimeType.startsWith('video/')) return 'Video';
    if (mimeType.includes('spreadsheetml')) return 'Excel';
    if (mimeType === 'application/pdf') return 'PDF';
    return 'Unknown';
  };

  const createPreviewUrl = (file: File) => {
    if (file && file.type.startsWith('image/')) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    } else {
      setPreviewUrl(null);
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    setIsLoading(true);
    try {
      if (!sharepointToken?.data?.token) {
        showErrorNotification({
          title: 'Token Error',
          message: 'SharePoint token not available. Please try again.'
        });
        return;
      }

      const base64Data = await fileToBase64(file);

      const uploadData: UploadSharepointForm = {
        sharepointToken: sharepointToken?.data?.token,
        folderName: 'materials',
        fileName: file.name,
        fileBase64: base64Data
      };

      const uploadResult = await uploadFile.mutateAsync(uploadData);

      if (onSuccess) {
        onSuccess({
          title: file.name, // Using filename as the title
          url: uploadResult?.data?.fileUrl || '',
          type: getFileType(file),
          description: ''
        });
      }

      // Reset state
      setFile(null);
      setPreviewUrl(null);
      setHasFileToUpload(false);

      if (onCancel) {
        onCancel();
      }
    } catch (_error) {
      showErrorNotification({
        title: 'Upload Error',
        message: `Failed to upload ${file.name}. Please try again.`
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Stack gap={16}>
      <Dropzone
        onDrop={(files) => {
          const selectedFile = files[0];
          setFile(selectedFile);
          createPreviewUrl(selectedFile);
          setHasFileToUpload(true);
        }}
        onReject={(files) => {
          showErrorNotification({
            title: 'File Rejected',
            message: `${files[0]?.file.name} was rejected. Please check file type and size.`
          });
        }}
        maxSize={3 * 1024 ** 2} // 3MB
        accept={acceptedTypes}
        loading={isLoading}
      >
        <Group justify='center' gap='xl' style={{ minHeight: 140, pointerEvents: 'none' }}>
          <Dropzone.Accept>
            <IconUpload size={50} stroke={1.5} />
          </Dropzone.Accept>
          <Dropzone.Reject>
            <IconX size={50} stroke={1.5} />
          </Dropzone.Reject>
          <Dropzone.Idle>
            {file ? (
              <Stack gap={8}>
                <Group justify='space-between'>
                  <Text size='sm' fw={500}>
                    {file.name}
                  </Text>
                  <Text size='xs' c='dimmed'>
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </Text>
                </Group>

                {previewUrl && <Image src={previewUrl} alt='Preview' maw={200} mx='auto' />}
              </Stack>
            ) : (
              <IconPhoto size={50} stroke={1.5} />
            )}
          </Dropzone.Idle>

          {!file && (
            <Stack gap={0} style={{ textAlign: 'center' }}>
              <Text size='xl' inline>
                Drag file here or click to select
              </Text>
              <Text size='sm' c='dimmed' inline mt={7}>
                File should not exceed 3MB
              </Text>
            </Stack>
          )}
        </Group>
      </Dropzone>

      <Group justify='flex-end' gap={12}>
        {onCancel && (
          <Button variant='outline' onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
        )}
        {file && (
          <Button
            onClick={() => {
              setFile(null);
              setPreviewUrl(null);
              setHasFileToUpload(false);
              if (onCancel) {
                onCancel();
              }
            }}
            disabled={isLoading}
            variant='subtle'
          >
            Remove Media
          </Button>
        )}
        <Button onClick={handleUpload} loading={isLoading} disabled={!file}>
          Upload Media
        </Button>
      </Group>
    </Stack>
  );
};

export default InputMaterial;
